import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, X, Users, Search, AlertCircle, Building2 } from 'lucide-react';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Checkbox } from './ui/checkbox';
import { CommanServices } from '@/services/commanServices';

const DepartmentSelector = ({
  value = null, // single: string, multi: array
  onChange = () => { },
  mode = 'single', // 'single' | 'multi'
  placeholder = 'Select department...',
  allowWildcard = false, // Enable "All Departments" wildcard option
  className = '',
  disabled = false,
  is_department_role = true,
  // PRIMARY TOGGLE props (multi mode only)
  primaryIds = [],           // array of dept IDs marked as primary
  onPrimaryToggle = null,    // (deptId, isPrimary) => {} — called when star is clicked
}) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch departments from api - EXACTLY like LocationSelector
  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("🚀 DepartmentSelector: Fetching departments (optimized endpoint)...");

      const response = await CommanServices.departmentslocationcounts();
      if (response?.status) {
        const data = response.data;
        const list = Array.isArray(data) ? data : [];
        console.log("✅ DepartmentSelector: Received", list.length, "departments");
        setDepartments(list);
      } else {
        console.warn("⚠️ DepartmentSelector: Optimized endpoint failed:", response?.message, "— trying fallback");
        const fallbackResponse = await CommanServices.departments();
        if (fallbackResponse?.status) {
          const fallbackData = fallbackResponse.data;
          const list = Array.isArray(fallbackData) ? fallbackData : [];
          console.log("🔄 DepartmentSelector: Fallback successful, received", list.length, "departments");
          setDepartments(list);
        } else {
          console.error("❌ DepartmentSelector: Fallback also failed:", fallbackResponse?.message);
          setError(fallbackResponse?.message || "Failed to load departments");
        }
      }
    } catch (err) {
      console.error("❌ DepartmentSelector: Fetch failed:", err);
      try {
        const fallbackResponse = await CommanServices.departments();
        if (fallbackResponse?.status) {
          const fallbackData = fallbackResponse.data;
          setDepartments(Array.isArray(fallbackData) ? fallbackData : []);
        } else {
          setError(fallbackResponse?.message || err?.message || "Failed to load departments");
        }
      } catch (fallbackErr) {
        console.error("❌ DepartmentSelector: Both endpoints failed:", fallbackErr);
        setError(fallbackErr?.message || "Failed to load departments");
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced search with 350ms delay
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter departments (no grouping - flat list)
  const filteredDepartments = useMemo(() => {
    if (!departments.length) return [];

    const filtered = departments.filter(department => {
      if (!debouncedSearchTerm) return true;
      const searchLower = debouncedSearchTerm.toLowerCase();
      return (
        (department.department_name || '').toLowerCase().includes(searchLower) ||
        (department.department_role || '').toLowerCase().includes(searchLower)
      );
    });

    // Sort departments alphabetically by name
    return filtered.sort((a, b) =>
      (a.department_name || '').localeCompare(b.department_name || '')
    );
  }, [departments, debouncedSearchTerm]);

  // Get selected departments for display - handle both department IDs and names
  const selectedDepartments = useMemo(() => {
    if (mode === 'single') {
      if (!value) return null;
      // Try to match by ID first (check both .id and .department_id), then by name for backwards compatibility
      return departments.find(dept =>
        dept.id === value || dept.department_id === value || dept.department_name === value
      ) || null;
    } else {
      if (!value || value.length === 0) return [];
      // Filter departments that match either by ID or name (check both .id and .department_id)
      return departments.filter(dept =>
        value.includes(dept.id) || value.includes(dept.department_id) || value.includes(dept.department_name)
      );
    }
  }, [value, departments, mode]);

  // Handle selection
  const handleSelect = (departmentId) => {
    if (mode === 'single') {
      onChange(departmentId);
      setOpen(false);
    } else {
      const currentValue = value || [];
      const newValue = currentValue.includes(departmentId)
        ? currentValue.filter(v => v !== departmentId)
        : [...currentValue, departmentId];
      onChange(newValue);
    }
  };

  // Handle wildcard selection
  const handleWildcardSelect = () => {
    if (mode === 'single') {
      onChange(''); // Empty string = wildcard
      setOpen(false);
    } else {
      onChange([]); // Empty array = wildcard
      setOpen(false);
    }
  };

  // Remove selected item (multi-select)
  const removeSelection = (departmentId, e) => {
    e.stopPropagation();
    if (mode === 'multi') {
      const newValue = (value || []).filter(v => v !== departmentId);
      onChange(newValue);
    }
  };

  // Display text for trigger button
  const getDisplayText = () => {
    if (allowWildcard && (!value || (Array.isArray(value) && value.length === 0))) {
      return '🌟 All Departments (wildcard)';
    }

    if (mode === 'single') {
      if (selectedDepartments) {
        return `${selectedDepartments.department_name} (${selectedDepartments.department_role || 'Department'})`;
      }
      return placeholder;
    } else {
      const count = selectedDepartments.length;
      if (count === 0) {
        // In multi-mode, if no selections and wildcard is allowed, show wildcard text
        return allowWildcard ? '🌟 All Departments (wildcard)' : placeholder;
      }
      if (count === 1) {
        return `${selectedDepartments[0].department_name} (${selectedDepartments[0].department_role || 'Department'})`;
      }
      return `${count} departments selected`;
    }
  };

  // Render status badge for department role
  const getRoleBadge = (role) => {
    const colors = {
      Command: 'bg-purple-100 text-purple-700',
      Response: 'bg-red-100 text-red-700',
      Support: 'bg-blue-100 text-blue-700',
      Medical: 'bg-green-100 text-green-700',
      Security: 'bg-yellow-100 text-yellow-700',
      Maintenance: 'bg-orange-100 text-orange-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 p-2 border rounded-md bg-gray-50 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Loading departments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-2 p-2 border border-red-200 rounded-md bg-red-50 ${className}`}>
        <AlertCircle className="h-4 w-4 text-red-500" />
        <span className="text-sm text-red-600">Error loading departments: {error}</span>
        <Button variant="ghost" size="sm" onClick={fetchDepartments}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${className}`}
          disabled={disabled}
        >
          <div className="flex items-center space-x-2 flex-1 text-left">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search departments..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <CommandList className="max-h-[300px]">
            {/* Multi-select: Show selected items as removable chips at top */}
            {mode === 'multi' && selectedDepartments.length > 0 && (
              <div className="p-2 border-b bg-blue-50">
                <div className="text-xs font-medium text-blue-800 mb-2">Selected ({selectedDepartments.length}):</div>
                <div className="flex flex-wrap gap-1">
                  {selectedDepartments.map((department) => {
                    const deptId = department.id || department.department_id;
                    const isPrimary = primaryIds.some(pid => pid === department.id || pid === department.department_id);
                    return (
                      <Badge
                        key={department.department_name}
                        variant="secondary"
                        className={`text-xs flex items-center gap-1 ${isPrimary ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-blue-100 text-blue-800'}`}
                      >
                        {/* Primary toggle */}
                        {onPrimaryToggle && (
                          <span
                            className="focus:outline-none flex items-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Switch
                              checked={isPrimary}
                              onCheckedChange={(checked) => onPrimaryToggle(department.id || department.department_id, checked)}
                              className="scale-75"
                            />
                          </span>
                        )}
                        {department.department_name}
                        {isPrimary && <span className="text-amber-600 font-semibold text-[10px] ml-0.5">Primary</span>}
                        <X
                          className="ml-1 h-3 w-3 cursor-pointer hover:text-red-600"
                          onClick={(e) => removeSelection(department.id, e)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Wildcard option */}
            {allowWildcard && (
              <CommandGroup heading="Wildcard Option">
                <CommandItem onSelect={handleWildcardSelect}>
                  <div className="flex items-center space-x-2 w-full">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {(!value || (Array.isArray(value) && value.length === 0)) && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-blue-700">🌟 All Departments (wildcard)</div>
                      <div className="text-xs text-blue-600">Apply to all current and future departments</div>
                    </div>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}

            {/* Empty state */}
            {filteredDepartments.length === 0 && !loading && (
              <CommandEmpty>
                <div className="text-center py-6">
                  <Users className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <div className="text-sm font-medium text-gray-900 mb-1">No departments yet</div>
                  <div className="text-xs text-gray-500 mb-3">
                    Create departments to populate this dropdown
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Navigate to Departments & Units - assuming this is available in parent context
                      window.location.hash = '#departments';
                    }}
                  >
                    <Building2 className="w-3 h-3 mr-1" />
                    Settings → Departments & Units
                  </Button>
                </div>
              </CommandEmpty>
            )}

            {/* Flat department options (no role grouping for simplicity) */}
            {filteredDepartments.length > 0 && (
              <CommandGroup>
                {filteredDepartments.map((department) => {
                  const isSelected = mode === 'single'
                    ? (value === department.id || value === department.department_name)
                    : (value || []).includes(department.id) || (value || []).includes(department.department_name);

                  const isPrimaryRow = primaryIds.some(pid => pid === department.id || pid === department.department_id);
                  return (
                    <CommandItem
                      key={department.department_name}
                      value={`${department.department_name} ${department.department_role || ''}`}
                      onSelect={() => handleSelect(department.id)}
                    >
                      <div className="flex items-center space-x-2 w-full">
                        {mode === 'multi' ? (
                          <Checkbox
                            checked={isSelected}
                            className="h-4 w-4"
                          />
                        ) : (
                          <div className="w-4 h-4 flex items-center justify-center">
                            {isSelected && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          {/* Primary: Department Name (prominent display) */}
                          <div className="font-semibold text-gray-900 truncate text-sm">
                            {department.department_name}
                          </div>
                          {/* Secondary: Role and stats */}
                          {is_department_role ?
                            <div className="text-xs text-gray-600 truncate mt-0.5">
                              {department.department_role || 'Department'} • {department.associated_locations?.length || 0} locations • {department.members?.length || 0} members
                            </div> : <></>}
                          {is_department_role ?
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge className={`text-xs ${getRoleBadge(department.department_role)}`}>
                                {department.department_role || 'Department'}
                              </Badge>
                            </div>
                            : <></>}
                        </div>

                        {/* Switch toggle — only visible for selected items */}
                        {mode === 'multi' && isSelected && onPrimaryToggle && (
                          <span
                            className="ml-2 flex items-center flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Switch
                              checked={isPrimaryRow}
                              onCheckedChange={(checked) => onPrimaryToggle(department.id || department.department_id, checked)}
                              className="scale-75"
                            />
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>

          {/* Helper text */}
          {allowWildcard && (
            <div className="border-t p-2 bg-gray-50">
              <div className="text-xs text-gray-600">
                💡 Leave blank to apply to all departments
              </div>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DepartmentSelector;